// Per-industry SEO overrides: high-intent B2B keywords, titles, descriptions,
// and the "Local SEO Multiplier" placement phrase used for city/state variants.
import type { IndustrySlug } from "./industries";

export type IndustrySeo = {
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  contentAngle: string;
  /** Root phrase used for "[phrase] in [City/State]" internal links. */
  placementPhrase: string;
};

export const INDUSTRY_SEO: Record<IndustrySlug, IndustrySeo> = {
  "offices-corporate": {
    seoTitle: "Free Office Vending Machine Placement | USA",
    seoDescription:
      "Free office vending machine services & corporate breakroom placement across the USA. Cashless snack, drink, coffee & micro-market machines — installed, restocked, and maintained at zero cost. Fast approval, 4–6 hr response.",
    keywords: [
      "free office vending machine services",
      "office snack machine placement",
      "corporate breakroom vending companies",
      "micro market setup for offices",
      "cashless office vending machines",
      "healthy office vending",
    ],
    contentAngle:
      "Boost employee satisfaction and HR perk scores with premium coffee, healthy snacks, and modern cashless machines — installed and serviced free.",
    placementPhrase: "office vending machine placement",
  },
  "warehouses-manufacturing": {
    seoTitle: "Warehouse & Plant Vending Placement | 24/7 USA",
    seoDescription:
      "Warehouse vending machine placement & manufacturing plant vending services nationwide. Heavy-duty, 24-hour employee breakroom vending — energy drinks, hydration & hearty snacks. Free installation, restocking & maintenance.",
    keywords: [
      "warehouse vending machine placement",
      "manufacturing plant vending services",
      "24 hour employee breakroom vending",
      "free industrial facility vending machine",
      "heavy duty vending machines",
      "distribution center vending",
    ],
    contentAngle:
      "24/7 uptime, fast restocking, and high-energy/hearty food and beverage options built for shift workers and manual laborers.",
    placementPhrase: "warehouse vending machine placement",
  },
  "hospitals-clinics": {
    seoTitle: "Hospital Vending Machine Placement | Healthy USA",
    seoDescription:
      "Hospital vending machine placement & medical center vending services nationwide. Healthy vending options for clinics, 24-hour hospital breakroom vending, silent operation & strict sanitation compliance — installed free.",
    keywords: [
      "hospital vending machine placement",
      "medical center vending services",
      "healthy vending options for clinics",
      "24 hour hospital breakroom vending",
      "compliant healthcare vending",
      "sanitized vending machines",
    ],
    contentAngle:
      "Sanitation, nutritional compliance, healthy and organic selections, and silent, non-disruptive machine operations for staff and families 24/7.",
    placementPhrase: "hospital vending machine placement",
  },
  "schools-universities": {
    seoTitle: "School & Campus Vending Machine Placement | USA",
    seoDescription:
      "University campus vending machine services, free high school vending machine placement & compliant school vending machines nationwide. Smart Snacks-aligned, high-capacity, cashless — installed and serviced free.",
    keywords: [
      "university campus vending machine services",
      "free high school vending machine placement",
      "compliant school vending machines",
      "college dorm vending services",
      "smart snacks vending",
      "student union vending",
    ],
    contentAngle:
      "Strict nutritional compliance (Smart Snacks in School), healthy snacking guidelines, and high-capacity machines for high-foot-traffic student zones.",
    placementPhrase: "school vending machine placement",
  },
  "hotels-hospitality": {
    seoTitle: "Hotel Vending Placement | Free Ice & Snacks USA",
    seoDescription:
      "Hotel guest vending machine placement & motel vending machine service provider nationwide. Hospitality industry vending services with free ice and snack machines for hotels — zero cost, rapid emergency maintenance.",
    keywords: [
      "hotel guest vending machine placement",
      "motel vending machine service provider",
      "hospitality industry vending services",
      "free ice and snack machines for hotels",
      "guest amenity vending",
      "lobby vending machines",
    ],
    contentAngle:
      "Zero-cost guest amenities, rapid emergency maintenance, aesthetics that match hotel decor, and ice/snack combinations that lift satisfaction scores.",
    placementPhrase: "hotel vending machine placement",
  },
  "gyms-fitness-centers": {
    seoTitle: "Gym Vending Placement | Protein & Sports Drinks USA",
    seoDescription:
      "Gym vending machine placement, fitness center protein shake vending & sports drink vending machines for gyms nationwide. Free fitness facility snack machines stocked with protein bars, pre-workout & hydration — zero cost.",
    keywords: [
      "gym vending machine placement",
      "fitness center protein shake vending",
      "sports drink vending machines for gyms",
      "free fitness facility snack machine",
      "protein bar vending",
      "pre-workout vending machines",
    ],
    contentAngle:
      "Specialized health inventory — protein bars, energy drinks, hydration supplements, and premium pre-workouts — placed free with no inventory risk.",
    placementPhrase: "gym vending machine placement",
  },
  "mines-mining-sites": {
    seoTitle: "Mine Site Vending Placement | 24/7 Heavy-Duty USA",
    seoDescription:
      "Mine site vending machine placement across the USA. Heavy-duty, 24/7 vending for mining crews — hydration, electrolytes, hearty snacks, and hot meals. Installed, restocked & maintained free.",
    keywords: [
      "mine vending machine placement",
      "mining site vending services",
      "24 hour crew breakroom vending",
      "remote industrial vending",
    ],
    contentAngle:
      "Rugged, remote-site vending built for mining crews — hydration, electrolytes, calorie-dense fuel, and 24/7 uptime.",
    placementPhrase: "mine vending machine placement",
  },
  "military-bases": {
    seoTitle: "Military Base Vending Machine Placement | USA",
    seoDescription:
      "Military base vending machine placement & defense facility vending services nationwide. Secure, compliant, 24/7 breakroom vending for personnel — installed, restocked, and maintained at zero cost.",
    keywords: [
      "military base vending machine placement",
      "defense facility vending services",
      "24 hour base breakroom vending",
      "secure vending machines",
    ],
    contentAngle:
      "Secure, credential-friendly, 24/7 vending for barracks, MWR spaces, and defense contractor facilities.",
    placementPhrase: "military base vending machine placement",
  },
  "airports-aviation": {
    seoTitle: "Airport Vending Placement | Aviation Facilities USA",
    seoDescription:
      "Airport vending machine placement & aviation facility vending services nationwide. 24/7 traveler and crew vending — cashless drinks, snacks, and grab-and-go — installed and serviced free.",
    keywords: [
      "airport vending machine placement",
      "aviation facility vending services",
      "24 hour traveler vending",
      "airside crew vending",
    ],
    contentAngle:
      "24/7 traveler and crew vending — cashless, TSA-friendly placements, rapid restocking for landside and airside zones.",
    placementPhrase: "airport vending machine placement",
  },
};
